// #123: whom a new project is assigned to once its role is chosen. A role held
// by one person gives the right answer without a click; with several holders,
// or none, nobody is guessed and the form says why.
export type MemberProposal =
  | { kind: 'single'; memberId: string }
  | { kind: 'several'; names: string[] }
  | { kind: 'vacant' }

interface Holder {
  id: string
  name: string
}

export function proposeMember(holders: readonly Holder[]): MemberProposal {
  const unique = holders.filter(
    (holder, index) => holders.findIndex((h) => h.id === holder.id) === index
  )
  if (unique.length === 1) return { kind: 'single', memberId: unique[0].id }
  if (unique.length === 0) return { kind: 'vacant' }
  return { kind: 'several', names: unique.map((holder) => holder.name) }
}

// The member after the role of a new project changes (#123, #149).
//
// - Picked by hand: never touched.
// - Empty, or this rule's own earlier proposal: replaced by the role's only
//   holder, or emptied.
// - Put by the screen (outside a meeting, oneself): kept while the role counts
//   them among its holders, otherwise treated like the case above — leaving
//   oneself on a role one does not hold assigned the project to the wrong
//   person without a word (#149).
interface RoleChange {
  current: string | null
  pickedByHand: boolean
  lastProposed: string | null
  holderIds: readonly string[]
  proposal: MemberProposal
}

export function memberAfterRoleChange({
  current,
  pickedByHand,
  lastProposed,
  holderIds,
  proposal,
}: RoleChange): string | null {
  if (pickedByHand) return current
  if (
    current !== null &&
    current !== lastProposed &&
    holderIds.includes(current)
  ) {
    return current
  }
  return proposal.kind === 'single' ? proposal.memberId : null
}
